import userTypes from "../constants/userTypes";
import menuList from "../constants/menuList";

/**
 * @typedef {{ label?: string, labelKey?: string, labelParams?: object, to?: string }} BreadcrumbItem
 * @typedef {{ crumbs: BreadcrumbItem[], subtitle?: string }} PageBreadcrumbResult
 */

const HOME_PATH = "/home";

/** @returns {BreadcrumbItem} */
const homeCrumb = () => ({ labelKey: "home_page", to: HOME_PATH });

/** @returns {BreadcrumbItem} */
const current = (labelKey, labelParams) => ({ labelKey, labelParams });

/** @returns {BreadcrumbItem} */
const link = (labelKey, to, labelParams) => ({ labelKey, to, labelParams });

/** @returns {BreadcrumbItem} */
const currentLabel = (label) => ({ label });

/** @returns {PageBreadcrumbResult} */
const result = (crumbs, subtitle) => ({ crumbs, subtitle });

/** @returns {PageBreadcrumbResult} */
const withHome = (parentCrumb, currentCrumb, subtitle) =>
  result([homeCrumb(), parentCrumb, currentCrumb], subtitle);

/** @returns {PageBreadcrumbResult} */
const homeOnly = (currentCrumb, subtitle) => result([currentCrumb], subtitle);

/** @returns {PageBreadcrumbResult} */
const pageWithHome = (currentCrumb, subtitle) =>
  result([homeCrumb(), currentCrumb], subtitle);

const APPOINTMENT_FROM_LABEL_BY_PATH = {
  "/appointments/calendar": "appointment_calendar",
  "/appointments/list": "my_appointments",
  "/appointments": "appointment_management",
  "/my-patients": "my_patients",
  "/doctor/appointments/calendar": "appointment_calendar",
  "/doctor/patients": "patients",
  "/notes": "patient_notes_management",
  "/home": "home_page",
};

const resolveFromLabelKey = (path, fallbackLabelKey) => {
  if (!path) return fallbackLabelKey;
  if (APPOINTMENT_FROM_LABEL_BY_PATH[path]) {
    return APPOINTMENT_FROM_LABEL_BY_PATH[path];
  }
  if (/^\/my-patients\/[^/]+\/schedule$/.test(path) || /^\/doctor\/patients\/[^/]+\/schedule$/.test(path)) {
    return "patient_schedule_page_title";
  }
  if (path.startsWith("/my-patients/") || path.startsWith("/doctor/patients/")) return "patient_detail_page_title";
  return fallbackLabelKey;
};

/**
 * Navigasyon state.from bilgisinden parent crumb üretir.
 * @param {object|string|undefined} from
 * @param {{ labelKey: string, to: string }} fallback
 * @returns {BreadcrumbItem}
 */
const parentFromNavigation = (from, fallback) => {
  if (!from) return link(fallback.labelKey, fallback.to);

  if (typeof from === "string") {
    const path = from.split("?")[0];
    return link(resolveFromLabelKey(path, fallback.labelKey), path || fallback.to);
  }

  const path = (from.path || from.to || fallback.to || "").split("?")[0];
  const labelKey = from.labelKey || resolveFromLabelKey(path, fallback.labelKey);

  if (from.label) {
    return { label: from.label, to: path || fallback.to };
  }
  return link(labelKey, path || fallback.to);
};

/**
 * @param {object} params
 * @param {string} params.pathname
 * @param {string} params.role
 * @param {function} params.t
 * @param {import('i18next').i18n} params.i18n
 * @param {string} [params.firstName]
 * @param {string} [params.lastName]
 * @param {object} [params.locationState]
 * @returns {PageBreadcrumbResult | null}
 */
export const getPageBreadcrumbs = ({
  pathname,
  role,
  t,
  i18n,
  firstName,
  lastName,
  locationState,
}) => {
  const normalizedRole = String(role || "").toLowerCase();
  const isDoctor = normalizedRole === userTypes.DOCTOR.toLowerCase();
  const isEmployee = normalizedRole === userTypes.EMPLOYEE.toLowerCase();
  const isAssistant = normalizedRole === userTypes.ASSISTANT.toLowerCase();

  const doctorStaticPages = {
    "/": homeOnly(
      currentLabel(isDoctor ? t("welcome_doctor", { firstName, lastName }) : ""),
      isDoctor ? t("welcome_to_patient_tracking") : ""
    ),
    "/home": homeOnly(
      currentLabel(isDoctor ? t("welcome_doctor", { firstName, lastName }) : ""),
      isDoctor ? t("welcome_to_patient_tracking") : ""
    ),
    "/appointments/calendar": pageWithHome(
      current("appointment_calendar"),
      isDoctor ? t("appointment_management_subtitle") : ""
    ),
    "/appointments/list": pageWithHome(
      current("my_appointments"),
      isDoctor ? t("my_appointments_subtitle") : ""
    ),
    "/appointments/settings": withHome(
      link("appointment_calendar", "/appointments/calendar"),
      current("appointment_settings"),
      isDoctor ? t("appointment_settings_subtitle") : ""
    ),
    "/tickets": pageWithHome(
      current("technical_support"),
      isDoctor ? t("technical_support_subtitle") : ""
    ),
    "/tickets/create": withHome(
      link(
        isDoctor ? "technical_support" : isEmployee ? "employee_ticket_list_title" : "help_and_support",
        "/tickets"
      ),
      current("create_new_request"),
      isDoctor ? t("create_new_request_subtitle") : ""
    ),
    "/messages": pageWithHome(
      current("messages"),
      isDoctor ? t("communicate_with_patients") : ""
    ),
    "/service-purchase": pageWithHome(
      current("service_purchases"),
      isDoctor ? t("service_purchases_subtitle") : ""
    ),
    "/service-package": pageWithHome(
      current("service_fees"),
      isDoctor ? t("service_fees_subtitle") : ""
    ),
    "/my-patients": pageWithHome(
      current("my_patients"),
      isDoctor ? t("my_patients_subtitle") : ""
    ),
    "/profile": pageWithHome(
      current("profile_edit"),
      isDoctor ? t("profile_edit_subtitle") : ""
    ),
    "/settings": pageWithHome(
      current("settings"),
      isDoctor ? t("settings_page_subtitle") : ""
    ),
    "/notes": pageWithHome(
      current("patient_notes_management"),
      isDoctor ? t("patient_notes_management_subtitle") : ""
    ),
    "/triage": pageWithHome(
      current("triage"),
      isDoctor ? t("triage_subtitle") : ""
    ),
    "/triage/add": withHome(
      link("triage", "/triage"),
      current("create_triage_form"),
      isDoctor ? t("create_triage_form_description") : ""
    ),
    "/user-status-info": pageWithHome(
      current("registration_pending"),
      isDoctor ? t("registration_pending_subtitle") : ""
    ),
    "/assistants": pageWithHome(
      current("assistants_page.assistants"),
      isDoctor ? t("assistants_page.assistants_subtitle") : ""
    ),
    "/assistants/add": withHome(
      link("assistants_page.assistants", "/assistants"),
      current("assistants_page.add_assistant"),
      isDoctor ? t("assistants_page.add_assistant_subtitle") : ""
    ),
    "/invoice-operations": pageWithHome(
      current("invoice_operations"),
      isDoctor ? t("invoice_operations_subtitle") : ""
    ),
    "/notifications": pageWithHome(
      current("notifications"),
      isDoctor ? t("notifications_subtitle") : ""
    ),
  };

  const employeeStaticPages = {
    "/": homeOnly(
      currentLabel(isEmployee ? t("employee_home_title") : ""),
      isEmployee ? t("employee_home_subtitle") : ""
    ),
    "/home": homeOnly(
      currentLabel(isEmployee ? t("employee_home_title") : ""),
      isEmployee ? t("employee_home_subtitle") : ""
    ),
    "/doctor": pageWithHome(
      current("employee_doctor_list_title"),
      isEmployee ? t("employee_doctor_list_subtitle") : ""
    ),
    "/patient": pageWithHome(
      current("employee_patient_list_title"),
      isEmployee ? t("employee_patient_list_subtitle") : ""
    ),
    "/employee": pageWithHome(
      current("employee_employee_list_title"),
      isEmployee ? t("employee_employee_list_subtitle") : ""
    ),
    "/price": pageWithHome(
      current("employee_price_title"),
      isEmployee ? t("employee_price_subtitle") : ""
    ),
    "/profile": pageWithHome(
      current("employee_profile_title"),
      isEmployee ? t("employee_profile_subtitle") : ""
    ),
    "/appointment": pageWithHome(
      current("employee_appointment_title"),
      isEmployee ? t("employee_appointment_subtitle") : ""
    ),
    "/tickets": pageWithHome(
      current("employee_ticket_list_title"),
      isEmployee ? t("employee_ticket_list_subtitle") : ""
    ),
    "/user-role": pageWithHome(
      current("employee_user_role_title"),
      isEmployee ? t("employee_user_role_subtitle") : ""
    ),
    "/specialists": pageWithHome(
      current("experts"),
      isEmployee ? t("expert_approval_subtitle") : ""
    ),
    "/languages": pageWithHome(
      current("languages"),
      isEmployee ? t("languages_management_description") : ""
    ),
    "/doctor-educations": pageWithHome(
      current("doctor_educations"),
      isEmployee ? t("doctor_educations_subtitle") : ""
    ),
    "/notification-message": pageWithHome(
      current("notification_messages"),
      isEmployee ? t("notification_messages_subtitle") : ""
    ),
    "/broadcast-notification": pageWithHome(
      current("broadcast_notification"),
      isEmployee ? t("broadcast_notification_subtitle") : ""
    ),
    "/audit-logs": pageWithHome(
      current("audit_logs"),
      isEmployee ? t("audit_logs_subtitle") : ""
    ),
    "/service-purchases": pageWithHome(
      current("service_purchases"),
      isEmployee ? t("service_purchases_subtitle") : ""
    ),
    "/service-refund-requests": pageWithHome(
      current("service_refund_requests"),
      isEmployee ? t("service_refund_requests_subtitle") : ""
    ),
    "/assistants": pageWithHome(
      current("assistants_page.assistants"),
      isEmployee ? t("assistants_page.assistants_subtitle") : ""
    ),
    "/assistants/add": withHome(
      link("assistants_page.assistants", "/assistants"),
      current("assistants_page.add_assistant"),
      isEmployee ? t("assistants_page.add_assistant_subtitle") : ""
    ),
  };

  const assistantStaticPages = {
    "/": homeOnly(
      currentLabel(isAssistant ? t("assistant_home_title") : ""),
      isAssistant ? t("welcome_assistant") : ""
    ),
    "/home": homeOnly(
      currentLabel(isAssistant ? t("assistant_home_title") : ""),
      isAssistant ? t("welcome_assistant") : ""
    ),
    "/doctor/appointments/calendar": pageWithHome(
      current("appointment_calendar"),
      isAssistant ? t("appointments_desc") : ""
    ),
    "/doctor/patients": pageWithHome(
      current("patients"),
      isAssistant ? t("assistant_patients_subtitle") : ""
    ),
    "/messages": pageWithHome(
      current("messages"),
      isAssistant ? t("messages_desc") : ""
    ),
    "/tickets": pageWithHome(
      current("help_and_support"),
      isAssistant ? t("help_and_support_desc") : ""
    ),
    "/tickets/create": withHome(
      link("help_and_support", "/tickets"),
      current("create_new_request"),
      isAssistant ? t("create_new_request_subtitle") : ""
    ),
    "/profile": pageWithHome(
      current("profile"),
      isAssistant ? t("profile_edit_subtitle") : ""
    ),
    "/settings": pageWithHome(
      current("settings"),
      isAssistant ? t("assistant_settings_page_subtitle") : ""
    ),
  };

  if (/^\/tickets\/[^/]+$/.test(pathname)) {
    const ticketParentKey = isEmployee
      ? "employee_ticket_list_title"
      : isAssistant
        ? "help_and_support"
        : "technical_support";
    return withHome(
      link(ticketParentKey, "/tickets"),
      current("ticket_details"),
      t("ticket_details_subtitle")
    );
  }

  if (/^\/service-purchase\/detail\/[^/]+$/.test(pathname) && isDoctor) {
    return withHome(
      link("service_purchases", "/service-purchase"),
      current("service_purchase_detail"),
      t("service_purchase_detail_subtitle")
    );
  }

  const patientScheduleMatch = pathname.match(/^\/(?:my-patients|doctor\/patients)\/([^/]+)\/schedule$/);
  if (patientScheduleMatch && (isDoctor || isAssistant)) {
    const patientsBase = isAssistant ? "/doctor/patients" : "/my-patients";
    return result(
      [
        homeCrumb(),
        link(isAssistant ? "patients" : "my_patients", patientsBase),
        link("patient_detail_page_title", `${patientsBase}/${patientScheduleMatch[1]}`),
        current("patient_schedule_page_title"),
      ],
      t("patient_schedule_page_subtitle")
    );
  }

  const patientDetailMatch = pathname.match(/^\/(?:my-patients|doctor\/patients)\/([^/]+)$/);
  if (patientDetailMatch && (isDoctor || isAssistant)) {
    const patientsBase = isAssistant ? "/doctor/patients" : "/my-patients";
    return withHome(
      link(isAssistant ? "patients" : "my_patients", patientsBase),
      current("patient_detail_page_title"),
      t("patient_detail_page_subtitle")
    );
  }

  const appointmentDetailMatch = pathname.match(/^\/appointments\/detail\/([^/]+)$/);
  if (appointmentDetailMatch && (isDoctor || isAssistant)) {
    const fallbackParent = {
      labelKey: "appointment_calendar",
      to: isAssistant ? "/doctor/appointments/calendar" : "/appointments/calendar",
    };
    return withHome(
      parentFromNavigation(locationState?.from, fallbackParent),
      current("appointment_detail"),
      t("appointment_detail_subtitle")
    );
  }

  const appointmentFilesMatch = pathname.match(/^\/appointments\/detail\/([^/]+)\/files$/);
  if (appointmentFilesMatch && (isDoctor || isAssistant)) {
    const fallbackParent = {
      labelKey: "appointment_calendar",
      to: isAssistant ? "/doctor/appointments/calendar" : "/appointments/calendar",
    };
    return result(
      [
        homeCrumb(),
        parentFromNavigation(locationState?.from, fallbackParent),
        link("appointment_detail", `/appointments/detail/${appointmentFilesMatch[1]}`),
        current("meeting_files"),
      ],
      t("meeting_detail")
    );
  }

  if (/^\/appointments\/meeting\/[^/]+$/.test(pathname) && isDoctor) {
    return withHome(
      link("appointment_calendar", "/appointments/calendar"),
      current("meeting_detail"),
      t("meeting_actions")
    );
  }

  if (/^\/appointments\/finalize\/[^/]+$/.test(pathname) && isDoctor) {
    return withHome(
      link("appointment_calendar", "/appointments/calendar"),
      current("meeting_detail"),
      t("appointment_detail_subtitle")
    );
  }

  const servicePackageFormMatch = pathname.match(/^\/service-package\/(add|edit\/[^/]+)$/);
  if (servicePackageFormMatch && isDoctor) {
    const isEdit = pathname.includes("/edit/");
    return withHome(
      link("service_fees", "/service-package"),
      current(isEdit ? "edit_service_fee" : "add_service_fee"),
      isEdit ? t("edit_service_fee_subtitle") : t("add_service_fee_subtitle")
    );
  }

  const triageQuestionFormMatch = pathname.match(/^\/triage\/([^/]+)\/question\/(add|edit\/[^/]+)$/);
  if (triageQuestionFormMatch && isDoctor) {
    const formId = triageQuestionFormMatch[1];
    const isEdit = pathname.includes("/edit/");
    return result(
      [
        homeCrumb(),
        link("triage", "/triage"),
        link("edit_triage_form", `/triage/${formId}`),
        current(isEdit ? "edit_question" : "new_question"),
      ],
      isEdit ? t("edit_triage_question_description") : t("create_new_triage_question_description")
    );
  }

  const triageFormMatch = pathname.match(/^\/triage\/([^/]+)$/);
  if (triageFormMatch && isDoctor && triageFormMatch[1] !== "add") {
    return withHome(
      link("triage", "/triage"),
      current("edit_triage_form"),
      t("edit_triage_form_description")
    );
  }

  if (/^\/messages(\/.*)?$/.test(pathname) && (isDoctor || isAssistant)) {
    const subtitle = isDoctor
      ? t("communicate_with_patients")
      : isAssistant
        ? t("messages_desc")
        : "";
    return pageWithHome(current("messages"), subtitle);
  }

  if (/^\/assistants\/edit\/[^/]+$/.test(pathname) && isDoctor) {
    return withHome(
      link("assistants_page.assistants", "/assistants"),
      current("assistants_page.edit_assistant"),
      t("assistants_page.edit_assistant_subtitle")
    );
  }

  if (isEmployee && /^\/user-role\/[^/]+$/.test(pathname)) {
    return withHome(
      link("employee_user_role_title", "/user-role"),
      current("employee_user_role_permission_title"),
      t("employee_user_role_permission_subtitle")
    );
  }

  if (isEmployee && /^\/doctor\/[^/]+\/profile$/.test(pathname)) {
    return withHome(
      link("employee_doctor_list_title", "/doctor"),
      current("employee_doctor_profile_title"),
      t("employee_doctor_profile_subtitle")
    );
  }

  const employeeAppointmentDateChangeMatch = pathname.match(/^\/appointment\/([^/]+)\/datechange$/);
  if (isEmployee && employeeAppointmentDateChangeMatch) {
    return result(
      [
        homeCrumb(),
        link("employee_appointment_title", "/appointment"),
        link("appointment_detail", `/appointment/${employeeAppointmentDateChangeMatch[1]}`),
        current("appointment_date_change"),
      ],
      t("appointment_date_change_subtitle")
    );
  }

  const employeeAppointmentDetailMatch = pathname.match(/^\/appointment\/([^/]+)$/);
  if (isEmployee && employeeAppointmentDetailMatch) {
    return withHome(
      link("employee_appointment_title", "/appointment"),
      current("appointment_detail"),
      t("appointment_detail_subtitle")
    );
  }

  if (isEmployee && /^\/specialists\/[^/]+$/.test(pathname)) {
    return withHome(
      link("experts", "/specialists"),
      current("expert_detail"),
      t("expert_detail_subtitle")
    );
  }

  const specialtyCategoryAttributesMatch = pathname.match(
    /^\/specialty-category-management\/([^/]+)\/attributes$/
  );
  if (isEmployee && specialtyCategoryAttributesMatch) {
    return withHome(
      link("specialty_category_management", "/specialty-category-management"),
      current("specialty_category_attributes"),
      t("specialty_category_attributes_subtitle")
    );
  }

  const specialtyCategoryBranchesMatch = pathname.match(
    /^\/specialty-category-management\/([^/]+)\/branches$/
  );
  if (isEmployee && specialtyCategoryBranchesMatch) {
    return withHome(
      link("specialty_category_management", "/specialty-category-management"),
      current("specialty_category_branches"),
      t("specialty_category_branches_subtitle")
    );
  }

  const specialtyCategoryTitlesMatch = pathname.match(
    /^\/specialty-category-management\/([^/]+)\/titles$/
  );
  if (isEmployee && specialtyCategoryTitlesMatch) {
    return withHome(
      link("specialty_category_management", "/specialty-category-management"),
      current("specialty_category_titles"),
      t("specialty_category_titles_subtitle")
    );
  }

  let staticPages = null;
  if (isDoctor) staticPages = doctorStaticPages;
  else if (isEmployee) staticPages = employeeStaticPages;
  else if (isAssistant) staticPages = assistantStaticPages;

  if (staticPages?.[pathname]) {
    const pageResult = staticPages[pathname];
    if (!pageResult.crumbs.some((crumb) => crumb.label || crumb.labelKey)) {
      return null;
    }
    return pageResult;
  }

  const roleKey =
    normalizedRole === userTypes.DOCTOR.toLowerCase()
      ? "doctor"
      : normalizedRole === userTypes.EMPLOYEE.toLowerCase()
        ? "employee"
        : normalizedRole === userTypes.ASSISTANT.toLowerCase()
          ? "assistant"
          : normalizedRole === userTypes.PATIENT.toLowerCase()
            ? "patient"
            : null;

  const firstSegment = pathname.replace(/^\/+/, "").split("/")[0];
  const roleMenu = roleKey ? menuList[roleKey] || [] : [];
  const matchingMenuItem = roleMenu.find((item) => {
    const targetPath = String(item?.to || "").replace(/^\/+/, "");
    return targetPath && (firstSegment === targetPath || pathname.includes(`/${targetPath}/`));
  });

  if (!matchingMenuItem?.text) {
    return null;
  }

  const subtitleKey = `${matchingMenuItem.text}_subtitle`;
  const descKey = `${matchingMenuItem.text}_desc`;
  const fallbackSubtitle = i18n.exists(subtitleKey)
    ? t(subtitleKey)
    : i18n.exists(descKey)
      ? t(descKey)
      : "";

  return pageWithHome(current(matchingMenuItem.text), fallbackSubtitle);
};

/**
 * @param {BreadcrumbItem} crumb
 * @param {function} t
 */
export const resolveBreadcrumbLabel = (crumb, t) => {
  if (crumb.label) return crumb.label;
  if (crumb.labelKey) return t(crumb.labelKey, crumb.labelParams);
  return "";
};
